import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Users, Receipt, Calendar, Scissors } from "lucide-react";
import { PageHeader, StatCard, inr } from "@/components/platform/platformUi";
import BackLink from "@/components/BackLink";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
}

export default function PlatformTenantDetail() {
    const { tenantId } = useParams();
    const [tenant, setTenant] = useState(null);
    const [plans, setPlans] = useState([]);
    const [busy, setBusy] = useState(false);
    const [extendDays, setExtendDays] = useState("");
    const [notes, setNotes] = useState("");
    const [planId, setPlanId] = useState("");
    const [subStatus, setSubStatus] = useState("trial");
    const [billingRows, setBillingRows] = useState([]);

    const load = async () => {
        try {
            const [t, p, bill] = await Promise.all([
                api.get(`/platform/tenants/${tenantId}`),
                api.get("/platform/plans"),
                api.get("/platform/billing", { params: { tenant_id: tenantId } }),
            ]);
            setTenant(t.data);
            setNotes(t.data?.internal_notes || "");
            setPlanId(t.data?.plan_id || "");
            setSubStatus(t.data?.subscription_status || "trial");
            setPlans(p.data || []);
            setBillingRows(bill.data || []);
        } catch {
            toast.error("Could not load salon details");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId]);

    const toggleStatus = async () => {
        if (!tenant) return;
        const next = tenant.status === "suspended" ? "active" : "suspended";
        setBusy(true);
        try {
            const r = await api.patch(`/platform/tenants/${tenantId}`, { status: next });
            setTenant(r.data);
            toast.success(`Salon ${next === "active" ? "resumed" : "suspended"}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Operation failed");
        } finally {
            setBusy(false);
        }
    };

    const extendTrial = async () => {
        const days = Number(extendDays);
        if (!days || days < 1) {
            toast.error("Enter extend days (1+)");
            return;
        }
        setBusy(true);
        try {
            const r = await api.patch(`/platform/tenants/${tenantId}`, { extend_trial_days: days });
            setTenant(r.data);
            setExtendDays("");
            toast.success("Trial extended");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Operation failed");
        } finally {
            setBusy(false);
        }
    };

    const saveNotes = async () => {
        setBusy(true);
        try {
            const r = await api.patch(`/platform/tenants/${tenantId}`, { internal_notes: notes });
            setTenant(r.data);
            toast.success("Internal notes saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save notes");
        } finally {
            setBusy(false);
        }
    };

    const saveSubscription = async () => {
        setBusy(true);
        try {
            const r = await api.patch(`/platform/tenants/${tenantId}`, {
                plan_id: planId || "",
                subscription_status: subStatus,
            });
            setTenant(r.data);
            toast.success("Subscription updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update subscription");
        } finally {
            setBusy(false);
        }
    };

    if (!tenant) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center p-6">
                <p className="font-serif text-xl text-[#8A8178]">Loading salon…</p>
            </div>
        );
    }

    const stats = tenant.stats || {};

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-salon-detail-page">
            <div className="flex flex-wrap items-center gap-3">
                <BackLink to="/platform/salons">Back to salons</BackLink>
            </div>

            <PageHeader
                eyebrow="Salon detail"
                title={tenant.name}
                subtitle={[tenant.city, tenant.slug, tenant.business_type].filter(Boolean).join(" · ")}
                action={
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            className="border-[#E8E4DE] bg-white text-[#3D2817] hover:border-[#D4AF37]/45"
                            disabled={busy}
                            onClick={toggleStatus}
                        >
                            {tenant.status === "suspended" ? "Resume salon" : "Suspend salon"}
                        </Button>
                    </div>
                }
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Customers" value={stats.customers ?? 0} Icon={Users} accent="#FBE4EF" iconColor="#D9468F" />
                <StatCard label="Staff" value={stats.staff ?? 0} Icon={Scissors} accent="#DCEEDC" iconColor="#2D9B6E" />
                <StatCard label="Appointments" value={stats.appointments ?? 0} Icon={Calendar} accent="#E0EDFA" iconColor="#3B82C4" />
                <StatCard label="Lifetime revenue" value={inr(stats.lifetime_revenue)} Icon={Receipt} />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <section className="rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)]">
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                        Account status
                    </p>
                    <dl className="mt-4 space-y-3 text-sm">
                        <div className="flex justify-between gap-4">
                            <dt className="font-medium text-[#8A8178]">Status</dt>
                            <dd className="font-semibold text-[#3D2817]">{tenant.status}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                            <dt className="font-medium text-[#8A8178]">Trial</dt>
                            <dd className="font-semibold text-[#3D2817]">{tenant.trial_status}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                            <dt className="font-medium text-[#8A8178]">Trial ends</dt>
                            <dd className="font-semibold text-[#3D2817]">{formatDate(tenant.trial_ends_at)}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                            <dt className="font-medium text-[#8A8178]">Subscription</dt>
                            <dd className="font-semibold capitalize text-[#3D2817]">{tenant.subscription_status || "trial"}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                            <dt className="font-medium text-[#8A8178]">Plan</dt>
                            <dd className="font-semibold text-[#3D2817]">{tenant.plan?.name || "None"}</dd>
                        </div>
                        {tenant.current_period_end && (
                            <div className="flex justify-between gap-4">
                                <dt className="font-medium text-[#8A8178]">Period ends</dt>
                                <dd className="font-semibold text-[#3D2817]">{formatDate(tenant.current_period_end)}</dd>
                            </div>
                        )}
                        {tenant.razorpay_subscription_id && (
                            <div className="flex justify-between gap-4">
                                <dt className="font-medium text-[#8A8178]">Razorpay sub</dt>
                                <dd className="font-mono text-xs text-[#3D2817]">{tenant.razorpay_subscription_id}</dd>
                            </div>
                        )}
                        {tenant.cancel_at_period_end && (
                            <div className="flex justify-between gap-4">
                                <dt className="font-medium text-[#8A8178]">Cancellation</dt>
                                <dd className="font-semibold text-[#C84B31]">At period end</dd>
                            </div>
                        )}
                        <div className="flex justify-between gap-4">
                            <dt className="font-medium text-[#8A8178]">Joined</dt>
                            <dd className="font-semibold text-[#3D2817]">{formatDate(tenant.created_at)}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                            <dt className="font-medium text-[#8A8178]">Invoices</dt>
                            <dd className="font-semibold text-[#3D2817]">{stats.invoices ?? 0}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                            <dt className="font-medium text-[#8A8178]">Team users</dt>
                            <dd className="font-semibold text-[#3D2817]">{stats.users ?? 0}</dd>
                        </div>
                    </dl>

                    <div className="mt-5 flex flex-wrap items-end gap-2 border-t border-[#E8E4DE] pt-4">
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Plan</Label>
                            <select
                                className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm"
                                value={planId}
                                onChange={(e) => setPlanId(e.target.value)}
                            >
                                <option value="">No plan</option>
                                {plans.filter((p) => p.active !== false).map((p) => (
                                    <option key={p.id} value={p.id}>{p.name} — {inr(p.price_monthly)}/mo</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Status</Label>
                            <select
                                className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm"
                                value={subStatus}
                                onChange={(e) => setSubStatus(e.target.value)}
                            >
                                <option value="trial">Trial</option>
                                <option value="active">Active</option>
                                <option value="past_due">Past due</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="expired">Expired</option>
                            </select>
                        </div>
                        <Button type="button" variant="outline" className="h-9" disabled={busy} onClick={saveSubscription}>
                            Save subscription
                        </Button>
                    </div>

                    <div className="mt-5 flex flex-wrap items-end gap-2 border-t border-[#E8E4DE] pt-4">
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">
                                Extend trial (days)
                            </Label>
                            <Input
                                type="number"
                                min={1}
                                className="h-9 w-28"
                                value={extendDays}
                                onChange={(e) => setExtendDays(e.target.value)}
                                placeholder="14"
                            />
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-9 border-[#E8E4DE] bg-white text-[#3D2817] hover:border-[#D4AF37]/45"
                            disabled={busy}
                            onClick={extendTrial}
                        >
                            Extend trial
                        </Button>
                    </div>
                </section>

                <section className="rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)]">
                    <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                        <Building2 className="size-3.5" />
                        Owner & team
                    </p>
                    {tenant.owner ? (
                        <div className="mt-4 rounded-lg border border-[#E8E4DE]/80 bg-[#FAF9F6] p-4">
                            <p className="text-sm font-semibold text-[#4A0E17]">{tenant.owner.full_name}</p>
                            <p className="text-sm text-[#3D2817]">{tenant.owner.email}</p>
                            {tenant.owner.mobile && (
                                <p className="text-sm text-[#8A8178]">{tenant.owner.mobile}</p>
                            )}
                        </div>
                    ) : (
                        <p className="mt-4 text-sm font-medium text-[#8A8178]">No owner on file</p>
                    )}

                    <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full border-collapse text-sm">
                            <thead>
                                <tr className="border-b border-[#E8E4DE] text-left text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8A8178]">
                                    <th className="py-2 pr-3">Name</th>
                                    <th className="py-2 pr-3">Role</th>
                                    <th className="py-2">Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(tenant.team || []).map((u) => (
                                    <tr key={u.id} className="border-b border-[#E8E4DE]/40 last:border-b-0">
                                        <td className="py-2 pr-3">
                                            <div className="font-semibold text-[#3D2817]">{u.full_name}</div>
                                            <div className="text-xs text-[#8A8178]">{u.email}</div>
                                        </td>
                                        <td className="py-2 pr-3 capitalize text-[#3D2817]">{u.role}</td>
                                        <td className="py-2">{u.active ? "Yes" : "No"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <section className="rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)]">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                    Subscription payments
                </p>
                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full border-collapse text-sm">
                        <thead>
                            <tr className="border-b border-[#E8E4DE] text-left text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8A8178]">
                                <th className="py-2 pr-3">Date</th>
                                <th className="py-2 pr-3">Plan</th>
                                <th className="py-2 pr-3">Amount</th>
                                <th className="py-2 pr-3">Status</th>
                                <th className="py-2">Gateway</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billingRows.map((r) => (
                                <tr key={r.id} className="border-b border-[#E8E4DE]/40 last:border-b-0">
                                    <td className="py-2 pr-3 text-[#8A8178]">{formatDate(r.paid_at || r.created_at)}</td>
                                    <td className="py-2 pr-3 font-medium text-[#3D2817]">{r.plan?.name || "—"}</td>
                                    <td className="py-2 pr-3 font-semibold text-[#4A0E17]">{inr(r.amount)}</td>
                                    <td className="py-2 pr-3 capitalize text-[#3D2817]">{r.status}</td>
                                    <td className="py-2 capitalize text-[#8A8178]">{r.payment_gateway}</td>
                                </tr>
                            ))}
                            {billingRows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="py-6 text-center text-[#8A8178]">No payments recorded</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)]">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                    Internal notes
                </p>
                <p className="mt-1 text-xs font-medium text-[#8A8178]">
                    Visible only to platform admins — not shown to salon owners
                </p>
                <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    className="mt-3 border-[#E8E4DE] bg-[#FFFEF9] text-sm"
                    placeholder="Support notes, billing context, onboarding follow-ups…"
                />
                <Button
                    type="button"
                    className="mt-3 bg-[#4A0E17] text-white hover:bg-[#3a0b12]"
                    disabled={busy}
                    onClick={saveNotes}
                >
                    Save notes
                </Button>
            </section>
        </div>
    );
}
